// Per-browser memory of preset, tab, the user's own vessel choice (SPEC §6.2), the side rail and
// each list's chosen columns; and per-user preferences kept on the server.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Preset, Tab } from "@po/shared";
import { api } from "./api";

type Prefs = {
  preset?: Preset;
  tab?: Tab;
  vessels?: string[] | null;
  railCollapsed?: boolean;
  /** Visible column keys per table; absent means the table's default. */
  columns?: Record<string, string[]>;
};
const KEY = "po.prefs";

export function readPrefs(): Prefs {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Prefs;
  } catch {
    return {};
  }
}

export function writePrefs(p: Prefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...readPrefs(), ...p }));
  } catch {
    // storage unavailable: nothing is remembered
  }
}

/**
 * A preference kept per user on the server (any browser), such as a grid's columns in order.
 * Undefined while loading, null when the user has none; `set(null)` returns to the default.
 */
export function useUserPref<T>(key: string): [T | null | undefined, (v: T | null) => void] {
  const qc = useQueryClient();
  const qk = ["user-pref", key];
  const { data } = useQuery({ queryKey: qk, queryFn: () => api<T | null>(`/prefs/${key}`), staleTime: Infinity });
  const set = (v: T | null) => {
    // Shown at once; the save follows, and a failed save leaves the choice for this visit only.
    qc.setQueryData(qk, v);
    void api(`/prefs/${key}`, { method: "PUT", body: JSON.stringify(v) }).catch(() => {});
  };
  return [data, set];
}
